// themeContext.tsx
import React, {createContext, useContext, useMemo, useState, ReactNode, useEffect} from "react";
import {ThemeProvider, CssBaseline} from "@mui/material";
import {darkTheme, lightTheme} from "./theme";

interface ThemeContextType {
    isDark: boolean;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Hook to access theme context
export const useThemeMode = () => {
    const context = useContext(ThemeContext);
    if (!context) throw new Error("useThemeMode must be used within ThemeModeProvider");
    return context;
};

interface ThemeModeProviderProps {
    children: ReactNode;
}

export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({children}) => {
    // Initialize theme from localStorage or default to false (light)
    const [isDark, setIsDark] = useState(() => {
        const storedTheme = localStorage.getItem("isDarkTheme");
        return storedTheme ? JSON.parse(storedTheme) : false;
    });

    const toggleTheme = () => setIsDark((prev: boolean) => !prev);

    // Update localStorage whenever theme changes
    useEffect(() => {
        localStorage.setItem("isDarkTheme", JSON.stringify(isDark));
    }, [isDark]);

    const theme = useMemo(() => (isDark ? darkTheme : lightTheme), [isDark]);
    const value = useMemo(() => ({isDark, toggleTheme}), [isDark]);

    return (
        <ThemeContext.Provider value={value}>
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};
